import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  Subject,
  throwError
} from 'rxjs';
import { ApiUrl } from './models/apiurl.models';
import { AppService } from '../app/services/app.service';
import { User } from '../app/models/user.model';
import { ApiService } from '../app/services/api.service';
import { ApiResponse } from '../app/models/api-response.model';
import { Page } from '../app/models/page.model';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private subject = new Subject<any>();
  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: this.apiUrl.getJWTToken()
    })
  };

  public url = 'api/users';

  constructor(
    public http: HttpClient,
    public apiUrl: ApiUrl,
    private apiService: ApiService,
    private appService: AppService
  ) {}

  private handleObserverError(error: any): Observable<any> {
    console.log(error);
    return throwError(error.error || error);
  }

  private suggestionTeachersSubject = new BehaviorSubject<User[]>([]);
  public suggestionTeachers$ = this.suggestionTeachersSubject.asObservable();

  // Fetch teachers from backend and store them
  loadSuggestionTeachers(): void {
    this.apiService.get<User[]>('api/users/teachers/').subscribe({
      next: (teachers: any) => {
        this.suggestionTeachersSubject.next(teachers);
      },
      error: (err: any) => {
        console.error('Failed to load teachers:', err);
      }
    });
  }

  // Get current value
  getTeacherList(): User[] {
    return this.suggestionTeachersSubject.value;
  }

  public_create_user(form: any): Observable<ApiResponse<Page[]>> {
    return this.apiService.post('api/users/register/', form);
  }
  create(form: any): Observable<ApiResponse<Page[]>> {
    return this.apiService.post('api/users/', form);
  }

  update(form: any, id: any): Observable<ApiResponse<Page[]>> {
    return this.apiService.put(`api/users/${id}/`, form);
  }

  delete(id: number): Observable<ApiResponse<Page[]>> {
    return this.apiService.delete(`api/users/${id}/`);
  }

  getUserDetails() {
    return this.http.get(this.apiUrl.getUserDetails(), this.options);
  }
  getIsSuperuser() {
    return this.http.get(this.apiUrl.getIsSuperuserUrl(), this.options);
  }

  async auth(registerForm: any) {
    try {
      const response = await firstValueFrom(
        this.http.post<any>(
          this.apiUrl.loginUser(),
          JSON.stringify(registerForm.value),
          this.options
        )
      );

      localStorage.setItem('token', response['user']['token']);
      localStorage.setItem('is_superuser', response['user']['is_superuser']);
      localStorage.setItem('first_name', response['user']['first_name']);
      localStorage.setItem('last_name', response['user']['last_name']);
      localStorage.setItem('email', response['user']['email']);
      localStorage.setItem('avatar', response['user']['avatar']);
      localStorage.setItem('phone_number', response['user']['phone_number']);
      localStorage.setItem('address', response['user']['address']);
      localStorage.setItem('role', response['user']['role']);
      if (response['user']['role'] == 'admin' || response['user']['role'] == 'teacher') {
        this.appService.setEditor(true);
      } else {
        this.appService.setEditor(false);
      }
      return response
    } catch (e) {
      return e
    }
  }

  signOut() {
    localStorage.removeItem('token');
    localStorage.removeItem('is_superuser');
    localStorage.removeItem('token');
    localStorage.removeItem('is_superuser');
    localStorage.removeItem('first_name');
    localStorage.removeItem('last_name');
    localStorage.removeItem('role');
    localStorage.removeItem('phone_number');
    localStorage.removeItem('address');
    localStorage.removeItem('email');
    localStorage.removeItem('avatar');
    localStorage.removeItem('userDetails');
  }

  getFirstName(): string | null {
    return localStorage.getItem('first_name');
  }

  getLastName(): string | null {
    return localStorage.getItem('last_name');
  }
  getAddress(): string | null {
    return localStorage.getItem('address');
  }

  getEmail(): string | null {
    return localStorage.getItem('email');
  }

  getAvatar(): string | null {
    return localStorage.getItem('avatar');
  }

  getPhoneNumber(): string | null {
    return localStorage.getItem('phone_number');
  }

  getFullName(): string {
    const first = this.getFirstName() || '';
    const last = this.getLastName() || '';
    return `${first} ${last}`.trim();
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  getRole(): string | null {
    return localStorage.getItem('role');
  }

  isSuperUser(): boolean {
    return localStorage.getItem('is_superuser') === 'true';
  }

  getUser(): any {
    return {
      token: this.getToken(),
      is_superuser: this.isSuperUser(),
      first_name: this.getFirstName(),
      last_name: this.getLastName(),
      email: this.getEmail(),
      avatar: this.getAvatar(),
      phoneNumber: this.getPhoneNumber(),
      fullName: this.getFullName()
    };
  }
}
